import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { getApiBaseUrl } from "@/lib/api-base";
import { withSkipGlobalError } from "@/lib/http/skip-global-error";
import { friendlyNetworkError } from "@/lib/http/network-error";
import { registerAccount, requestPasswordReset as apiRequestPasswordReset } from "@/lib/identity/identity-api";
import { normalizeAppRole } from "@/lib/auth/roles";

const AuthContext = createContext(null);
const API = getApiBaseUrl();

function persistTokens(accessToken, refreshToken) {
  if (!accessToken) {
    localStorage.removeItem("vedaPath_token");
    localStorage.removeItem("vedaPath_refresh");
    return;
  }
  localStorage.setItem("vedaPath_token", accessToken);
  if (refreshToken) localStorage.setItem("vedaPath_refresh", refreshToken);
}

function mapWorkerUser(user) {
  if (!user) return null;
  return {
    id: user.id,
    email: user.email,
    name: user.name || user.email?.split("@")[0] || "Seeker",
    role: normalizeAppRole(user.role),
    seeker_state: user.seeker_state || "curious",
    email_verified: user.email_verified !== false,
    language: user.language || user.preferred_language || "en",
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const applySession = useCallback((accessToken, refreshToken, profile) => {
    persistTokens(accessToken || null, refreshToken || null);
    setToken(accessToken || null);
    setUser(profile ? mapWorkerUser(profile) : null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function hydrate() {
      const access = localStorage.getItem("vedaPath_token");
      if (!access) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${access}` },
          ...withSkipGlobalError(),
        });
        if (cancelled) return;
        applySession(access, localStorage.getItem("vedaPath_refresh"), res.data?.user || res.data); // /auth/me returns profile JSON
      } catch {
        if (!cancelled) applySession(null, null, null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    hydrate();
    return () => {
      cancelled = true;
    };
  }, [applySession]);

  const signIn = useCallback(
    async (email, password, turnstileToken) => {
      try {
        const res = await axios.post(
          `${API}/auth/login`,
          {
            email: email.trim().toLowerCase(),
            password,
            turnstile_token: turnstileToken || undefined,
          },
          withSkipGlobalError()
        );
        const access = res.data.access_token || res.data.token;
        if (!access) throw new Error("Sign-in failed. Please try again.");
        applySession(access, res.data.refresh_token, res.data.user);
        return { access_token: access, user: mapWorkerUser(res.data.user), ...res.data };
      } catch (err) {
        const apiMsg = err.response?.data?.error || err.response?.data?.detail;
        throw new Error(
          typeof apiMsg === "string" && apiMsg.trim()
            ? apiMsg
            : friendlyNetworkError(err, "Sign-in failed.")
        );
      }
    },
    [applySession]
  );

  const signUp = useCallback(
    async (email, password, name, role = "user", preferred_language = "en", turnstileToken) => {
      try {
        const result = await registerAccount({
          email,
          password,
          name,
          role,
          preferred_language,
          turnstile_token: turnstileToken,
        });

        if (result.access_token || result.token) {
          applySession(result.access_token || result.token, result.refresh_token, result.user);
        }

        if (result.email_sent === false && result.needs_verification) {
          const reason =
            typeof result.email_reason === "string" ? result.email_reason : "Email delivery failed.";
          throw new Error(`Account created but verification email failed (${reason}).`);
        }

        return result;
      } catch (err) {
        if (err.response) {
          const apiMsg = err.response?.data?.error || err.response?.data?.detail;
          throw new Error(typeof apiMsg === "string" ? apiMsg : friendlyNetworkError(err));
        }
        throw err;
      }
    },
    [applySession]
  );

  const signOut = useCallback(async () => {
    const access = localStorage.getItem("vedaPath_token");
    try {
      if (access) {
        await axios.post(
          `${API}/auth/logout`,
          {},
          { headers: { Authorization: `Bearer ${access}` }, ...withSkipGlobalError() }
        );
      }
    } catch {
      /* ignore */
    }
    applySession(null, null, null);
  }, [applySession]);

  const requestPasswordReset = useCallback(async (email) => {
    return apiRequestPasswordReset(email);
  }, []);

  const updatePassword = useCallback(async (password, resetToken) => {
    if (resetToken) {
      const res = await axios.post(
        `${API}/auth/reset-password`,
        { token: resetToken, password },
        withSkipGlobalError()
      );
      return res.data;
    }
    const access = localStorage.getItem("vedaPath_token");
    if (!access) throw new Error("Not signed in.");
    const res = await axios.post(
      `${API}/auth/change-password`,
      { password, current_password: "" },
      { headers: { Authorization: `Bearer ${access}` }, ...withSkipGlobalError() }
    );
    return res.data;
  }, []);

  const value = {
    user,
    token,
    loading,
    applySession,
    signIn,
    signUp,
    signOut,
    requestPasswordReset,
    updatePassword,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuthContext must be used within AuthProvider");
  return ctx;
}
